import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type FontSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl'

const SIZE_MAP: Record<FontSize, string> = {
  xs: '12px',
  sm: '14px',
  base: '16px',
  lg: '18px',
  xl: '20px',
}

export const LABELS: Record<FontSize, string> = { xs: 'XS', sm: 'S', base: 'M', lg: 'L', xl: 'XL' }

const ALL_SIZES: FontSize[] = ['xs', 'sm', 'base', 'lg', 'xl']

const STORAGE_KEY = 'app_font_size'

interface Ctx {
  fontSize: FontSize
  setFontSize: (s: FontSize) => void
  label: string
  allSizes: FontSize[]
}

const FontSizeContext = createContext<Ctx>({
  fontSize: 'base',
  setFontSize: () => {},
  label: 'M',
  allSizes: ALL_SIZES,
})

export function FontSizeProvider({ children }: { children: ReactNode }) {
  const [fontSize, setFontSizeState] = useState<FontSize>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (ALL_SIZES.includes(stored as FontSize)) return stored as FontSize
    return 'base'
  })

  useEffect(() => {
    document.documentElement.style.fontSize = SIZE_MAP[fontSize]
    localStorage.setItem(STORAGE_KEY, fontSize)
  }, [fontSize])

  const setFontSize = (s: FontSize) => setFontSizeState(s)

  return (
    <FontSizeContext.Provider value={{ fontSize, setFontSize, label: LABELS[fontSize], allSizes: ALL_SIZES }}>
      {children}
    </FontSizeContext.Provider>
  )
}

export function useFontSize() {
  return useContext(FontSizeContext)
}
